import { useState, useCallback } from "react";
import { useBackButton } from "@/hooks/useBackButton";
import { useCart } from "@/contexts/CartContext";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Minus, Plus, Trash2, ShoppingBag, AlertTriangle } from "lucide-react";
import CheckoutDialog from "@/components/CheckoutDialog";

const CartDrawer = () => {
  const { items, isOpen, setIsOpen, removeItem, updateQuantity, clearCart, totalPrice } = useCart();
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const closeCart = useCallback(() => setIsOpen(false), [setIsOpen]);
  const closeCheckout = useCallback(() => setCheckoutOpen(false), []);
  useBackButton(isOpen, closeCart, "cart");
  useBackButton(checkoutOpen, closeCheckout, "checkout");

  const formatPrice = (value: number) =>
    value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

  return (
    <>
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent className="bg-card border-l border-border w-full sm:max-w-md flex flex-col">
        <SheetHeader>
          <SheetTitle className="font-display text-xl text-foreground flex items-center gap-2">
            <ShoppingBag className="w-5 h-5 text-primary" />
            Seu Carrinho
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center gap-4">
            <ShoppingBag className="w-16 h-16 text-muted-foreground/30" />
            <p className="text-muted-foreground">Seu carrinho está vazio</p>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto space-y-4 py-4">
              {items.map((item) => (
                <div key={item.id} className="flex gap-3 bg-secondary rounded-lg p-3">
                  <img src={item.image} alt={item.name} className="w-16 h-16 rounded-lg object-cover" />
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-semibold text-foreground truncate">{item.name}</h4>
                    <p className="text-sm font-bold text-gradient-gold">{item.price}</p>
                    {item.id.startsWith("seguro-") && (
                      <span className="text-xs text-primary font-medium">Sem juros • Valor fixo mensal</span>
                    )}
                    <div className="flex items-center gap-2 mt-2">
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        className="w-7 h-7 rounded-md border border-border flex items-center justify-center text-foreground hover:border-primary transition-colors"
                      >
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="text-sm font-medium text-foreground w-6 text-center">{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        className="w-7 h-7 rounded-md border border-border flex items-center justify-center text-foreground hover:border-primary transition-colors"
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => removeItem(item.id)}
                        className="ml-auto w-7 h-7 rounded-md flex items-center justify-center text-destructive hover:bg-destructive/10 transition-colors"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-border pt-4 space-y-4">
              <div className="rounded-lg bg-primary/10 border border-primary/20 p-3 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                <p className="text-xs text-foreground">
                  Para pagamentos via <strong>Carnê</strong>, a contratação do{" "}
                  <strong>Seguro Premium (R$ 49,99/mês)</strong> é obrigatória.
                </p>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-foreground font-semibold">Total</span>
                <span className="text-2xl font-bold text-gradient-gold">{formatPrice(totalPrice)}</span>
              </div>
              <button
                onClick={() => { setIsOpen(false); setCheckoutOpen(true); }}
                className="w-full py-3 rounded-lg bg-gradient-gold text-primary-foreground font-semibold hover:opacity-90 transition-opacity"
              >
                Finalizar Compra
              </button>
              <button
                onClick={clearCart}
                className="w-full py-2 text-sm text-muted-foreground hover:text-destructive transition-colors"
              >
                Limpar carrinho
              </button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
    <CheckoutDialog open={checkoutOpen} onOpenChange={setCheckoutOpen} />
    </>
  );
};

export default CartDrawer;
